import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSuccessToast } from "@hooks/useSuccessToast";
import type { JobViewModel } from "@models/job";
import type { JobSubmitArgs } from "@hooks/useJobSubmit";

interface UseMappingJobActionsArgs {
  parsed: { kind: string; pluginId: string } | null;
  serviceId: string | undefined;
  jobs: JobViewModel[];
  submit: (args: JobSubmitArgs) => Promise<JobViewModel | null>;
  retry: () => Promise<JobViewModel | null>;
  upsertJob: (job: JobViewModel) => void;
  deleteJob: (jobId: string) => Promise<boolean>;
}

export interface MappingJobActions {
  handleDeleteJob: (jobId: string) => Promise<boolean>;
  handleFilesSelected: (files: File[]) => Promise<void>;
  handleRetry: () => Promise<void>;
  openJob: (jobId: string) => void;
}

/**
 * Job-list actions for the mapping service page: upload (one or many files),
 * retry a failed upload, open a job, and delete with a success toast.
 * Each newly created job is optimistically upserted into the list.
 */
export const useMappingJobActions = ({
  parsed,
  serviceId,
  jobs,
  submit,
  retry,
  upsertJob,
  deleteJob,
}: UseMappingJobActionsArgs): MappingJobActions => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deletedNotice, setDeletedNotice] = useState<string | null>(null);

  useSuccessToast(
    deletedNotice,
    t("mappingServicePage.jobs.deleteSuccessTitle"),
    () => setDeletedNotice(null),
  );

  const handleDeleteJob = useCallback(
    async (jobId: string): Promise<boolean> => {
      const removed = jobs.find((j) => j.jobId === jobId);
      const ok = await deleteJob(jobId);
      if (ok) {
        setDeletedNotice(
          t("mappingServicePage.jobs.deleteSuccess", {
            filename: removed?.filename ?? "",
          }),
        );
      }
      return ok;
    },
    [jobs, deleteJob, t],
  );

  const handleFileSelected = useCallback(
    async (file: File | null): Promise<void> => {
      if (!file || !parsed) return;
      const initial = await submit({
        service: parsed.kind,
        pluginId: parsed.pluginId,
        file,
      });
      if (initial) upsertJob(initial);
    },
    [parsed, submit, upsertJob],
  );

  const handleFilesSelected = useCallback(
    async (files: File[]): Promise<void> => {
      for (const file of files) {
        await handleFileSelected(file);
      }
    },
    [handleFileSelected],
  );

  const handleRetry = useCallback(async (): Promise<void> => {
    const initial = await retry();
    if (initial) upsertJob(initial);
  }, [retry, upsertJob]);

  const openJob = useCallback(
    (jobId: string): void => {
      if (!serviceId) return;
      navigate(`jobs/${encodeURIComponent(jobId)}`);
    },
    [navigate, serviceId],
  );

  return { handleDeleteJob, handleFilesSelected, handleRetry, openJob };
};
